import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet.js";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import Point from "@arcgis/core/geometry/Point";
import {
  locationDetailsTask,
  removalReportTask,
  simplePolygonFields,
  simpleTreeFields,
  simpleTreeFieldNames,
  reportEndpoint,
  adminReportEndpoint,
  exportShapefileTask,
  offsetReportTask,
  removalUploadValidationTask,
  offsetUploadValidationTask,
  detailedRemovalPatchFieldNames,
  detailedRemovalPatchFields,
  detailedRemovalTreeFields,
  detailedRemovalTreeFieldNames,
  detailedOffsetTreeFieldNames,
  detailedOffsetPatchFieldNames,
  detailedOffsetPatchFields,
  detailedOffsetTreeFields,
  detailedRevegetationFieldNames,
  detailedRevegetationFields,
  adminExportEndpoint,
  locationResultsToFetch,
  removalResultsToFetch,
  offsetResultsToFetch,
} from "../lib/gpConfig";
import {
  Patch,
  RevegetationArea,
  Tree,
  featureTypes,
  nvrToolModes,
  nvrTools,
  treeCmType,
  treeType,
  uploadTypes,
  uploadTypesOption,
} from "../lib/types";
import { fetchAuthSession } from "aws-amplify/auth";
import { requestTimeout } from "../lib/constants";

const treePropertyMapping = {
  treeType: { [treeType.Patch]: "pt", [treeType.Scattered]: "st" },
  cmType: { [treeCmType.CIR]: "cir", [treeCmType.DBH]: "dbh" },
};

const createGraphicAttrsFromAttrsList = (feature: any, attrsList: string[]) => {
  const test = attrsList.reduce(
    (attrs: any, attr: string) => ({ ...attrs, [attr]: feature[attr] }),
    {}
  );
  return test;
};

const getSimplePolygonFeatureSet = (features: any[]) =>
  new FeatureSet({
    features: features.map(
      (x) =>
        new Graphic({
          geometry: new Polygon(x.geom),
          attributes: { id: x.id, type: x.type },
        })
    ),
    fields: simplePolygonFields,
  });

const getSimpleTreesFeatureSet = (trees: Tree[]) =>
  new FeatureSet({
    features: trees.map(
      (x) =>
        new Graphic({
          geometry: new Point(x.geom),
          attributes: {
            id: x.id,
            [simpleTreeFieldNames.treeType]:
              treePropertyMapping.treeType[x.treeType],
            [simpleTreeFieldNames.cmType]:
              treePropertyMapping.cmType[x.cmType as treeCmType],
            [simpleTreeFieldNames.cm]: x.cm,
            [simpleTreeFieldNames.vegCode]: x.vegCode,
            [simpleTreeFieldNames.benchmark]: x.benchmark,
          },
        })
    ),
    fields: simpleTreeFields,
  });

const getDetailedFeatureSet = (
  features: any[],
  featureType: featureTypes,
  fieldNames: any,
  fields: any
) => {
  let filteredFieldNames: string[];
  let filteredFields: any[];

  if (features.length === 0) {
    filteredFieldNames = Object.values(fieldNames);
    filteredFields = fields;
  } else {
    // Filter the fields to only those contained in the first feature (all features will have same schema)
    const featureFieldNames = Object.keys(features[0]) as string[];
    const fieldNameValues = Object.values(fieldNames) as string[];
    filteredFieldNames = fieldNameValues.filter(
      (x) => featureFieldNames.indexOf(x) !== -1
    );
    filteredFields = fields.filter(
      (x: any) => featureFieldNames.indexOf(x.name) !== -1
    );
  }

  return new FeatureSet({
    features: features.map(
      (x) =>
        new Graphic({
          geometry:
            featureType === featureTypes.tree
              ? new Point(x.geom)
              : new Polygon(x.geom),
          attributes: createGraphicAttrsFromAttrsList(x, filteredFieldNames),
        })
    ),
    fields: filteredFields,
  });
};

const createFeatureSets = (
  patches: Patch[],
  trees: Tree[],
  revegetationAreas: RevegetationArea[],
  nvrToolMode: nvrToolModes,
  uploadType: uploadTypesOption
) => {
  let patchesFeatureSet, treesFeatureSet, revegAreasFeatureSet;
  if (uploadType === uploadTypes.simple) {
    patchesFeatureSet = getSimplePolygonFeatureSet(patches);
    treesFeatureSet = getSimpleTreesFeatureSet(trees);
    revegAreasFeatureSet = getSimplePolygonFeatureSet(revegetationAreas);
  } else {
    const patchFieldNames =
      nvrToolMode === nvrTools.removal
        ? detailedRemovalPatchFieldNames
        : detailedOffsetPatchFieldNames;
    const patchFields =
      nvrToolMode === nvrTools.removal
        ? detailedRemovalPatchFields
        : detailedOffsetPatchFields;
    const treeFieldNames =
      nvrToolMode === nvrTools.removal
        ? detailedRemovalTreeFieldNames
        : detailedOffsetTreeFieldNames;
    const treeFields =
      nvrToolMode === nvrTools.removal
        ? detailedRemovalTreeFields
        : detailedOffsetTreeFields;
    patchesFeatureSet = getDetailedFeatureSet(
      patches,
      featureTypes.patch,
      patchFieldNames,
      patchFields
    );
    treesFeatureSet = getDetailedFeatureSet(
      trees,
      featureTypes.tree,
      treeFieldNames,
      treeFields
    );
    revegAreasFeatureSet = getDetailedFeatureSet(
      revegetationAreas,
      featureTypes.revegetationArea,
      detailedRevegetationFieldNames,
      detailedRevegetationFields
    );
  }
  return {
    patchesFeatureSet,
    treesFeatureSet,
    revegAreasFeatureSet,
  };
};

export const exportShapefile = async (
  patches: Patch[],
  trees: Tree[],
  revegAreas: RevegetationArea[],
  nvrToolMode: nvrToolModes,
  uploadType: uploadTypesOption
) => {
  const { patchesFeatureSet, treesFeatureSet, revegAreasFeatureSet } =
    createFeatureSets(patches, trees, revegAreas, nvrToolMode, uploadType);

  const result = await geoprocessor.execute(
    exportShapefileTask,
    {
      patches: patchesFeatureSet.toJSON(),
      trees: treesFeatureSet.toJSON(),
      revegetationAreas: revegAreasFeatureSet.toJSON(),
    },
    undefined,
    { timeout: requestTimeout }
  );
  return result;
};

export const validateDetailedUpload = async (
  nvrToolMode: nvrToolModes,
  patches: Patch[],
  trees: Tree[],
  revegAreas: RevegetationArea[]
) => {
  const { patchesFeatureSet, treesFeatureSet, revegAreasFeatureSet } =
    createFeatureSets(
      patches,
      trees,
      revegAreas,
      nvrToolMode,
      uploadTypes.detailed
    );

  let task = removalUploadValidationTask;

  const payload: any = {
    patches: patchesFeatureSet.toJSON(),
    trees: treesFeatureSet.toJSON(),
  };
  if (nvrToolMode === nvrTools.offset) {
    payload.revegetationAreas = revegAreasFeatureSet.toJSON();
    task = offsetUploadValidationTask;
  }

  const jobInfo = await geoprocessor.submitJob(task, payload);

  const options = {
    interval: 1500,
  };

  await jobInfo.waitForJobCompletion(options);

  let results: Record<string, any> = {};
  if (jobInfo.jobStatus === "job-succeeded") {
    try {
      const gpResult = await jobInfo.fetchResultData("validationFailures");
      results["messages"] = gpResult;
    } catch (err) {
      results["messages"] = null;
    }
  }

  return {
    jobId: jobInfo.jobId,
    results: results,
  };
};

const fetchLocationDetails = async (
  patches: FeatureSet,
  trees: FeatureSet,
  revegAreas: FeatureSet | undefined
) => {
  const params = {
    patches: patches.toJSON(),
    trees: trees.toJSON(),
    revegetationAreas: revegAreas ? revegAreas.toJSON() : [],
  };

  const jobInfo = await geoprocessor.submitJob(locationDetailsTask, params);

  const options = {
    interval: 1500,
  };

  await jobInfo.waitForJobCompletion(options);

  let results: Record<string, any> = {};
  if (jobInfo.jobStatus === "job-succeeded") {
    for (const resultName of locationResultsToFetch) {
      try {
        const gpResult = await jobInfo.fetchResultData(resultName);
        results[resultName] = gpResult;
      } catch (err) {
        results[resultName] = null;
      }
    }
  }

  return {
    jobId: jobInfo.jobId,
    results: results,
  };
};

const fetchRemovalDetails = async (
  patches: FeatureSet,
  trees: FeatureSet,
  pastRemovalArea: number | undefined,
  uploadType: uploadTypesOption
) => {
  const jobInfo = await geoprocessor.submitJob(removalReportTask, {
    patches: patches.toJSON(),
    trees: trees.toJSON(),
    pastRemovalArea: pastRemovalArea,
    includeOffsetResults: true,
    areFeaturesDetailed: uploadType === uploadTypes.detailed,
  });

  await jobInfo.waitForJobCompletion();

  const options = {
    interval: 1500,
  };

  await jobInfo.waitForJobCompletion(options);

  let results: Record<string, any> = {};
  if (jobInfo.jobStatus === "job-succeeded") {
    for (const resultName of removalResultsToFetch) {
      try {
        const gpResult = await jobInfo.fetchResultData(resultName);
        results[resultName] = gpResult;
      } catch (err) {
        results[resultName] = null;
      }
    }
  }

  return {
    jobId: jobInfo.jobId,
    results: results,
  };
};

const fetchOffsetDetails = async (
  patches: FeatureSet,
  trees: FeatureSet,
  revegetationAreas: FeatureSet,
  uploadType: uploadTypesOption
) => {
  const jobInfo = await geoprocessor.submitJob(offsetReportTask, {
    patches: patches.toJSON(),
    trees: trees.toJSON(),
    revegetationAreas: revegetationAreas.toJSON(),
    includeGainResults: true,
    areFeaturesDetailed: uploadType === uploadTypes.detailed,
  });

  await jobInfo.waitForJobCompletion();

  const options = {
    interval: 1500,
  };

  await jobInfo.waitForJobCompletion(options);

  let results: Record<string, any> = {};
  if (jobInfo.jobStatus === "job-succeeded") {
    for (const resultName of offsetResultsToFetch) {
      try {
        const gpResult = await jobInfo.fetchResultData(resultName);
        results[resultName] = gpResult;
      } catch (err) {
        results[resultName] = null;
      }
    }
  }

  return {
    jobId: jobInfo.jobId,
    results: results,
  };
};

export const fetchRemovalSummary = async (
  patches: Patch[],
  trees: Tree[],
  pastRemovalArea: number | undefined,
  uploadType: uploadTypesOption
) => {
  const { patchesFeatureSet, treesFeatureSet, revegAreasFeatureSet } =
    createFeatureSets(patches, trees, [], nvrTools.removal, uploadType);

  return Promise.all([
    fetchLocationDetails(
      patchesFeatureSet,
      treesFeatureSet,
      revegAreasFeatureSet
    ),
    fetchRemovalDetails(
      patchesFeatureSet,
      treesFeatureSet,
      pastRemovalArea,
      uploadType
    ),
  ]);
};

export const fetchOffsetSummary = async (
  patches: Patch[],
  trees: Tree[],
  revegAreas: RevegetationArea[],
  uploadType: uploadTypesOption
) => {
  const { patchesFeatureSet, treesFeatureSet, revegAreasFeatureSet } =
    createFeatureSets(patches, trees, revegAreas, nvrTools.offset, uploadType);

  return Promise.all([
    fetchLocationDetails(
      patchesFeatureSet,
      treesFeatureSet,
      revegAreasFeatureSet
    ),
    fetchOffsetDetails(
      patchesFeatureSet,
      treesFeatureSet,
      revegAreasFeatureSet,
      uploadType
    ),
  ]);
};

const getAuthHeaders = async () => {
  const { idToken } = (await fetchAuthSession()).tokens ?? {};
  if (!idToken) {
    return undefined;
  }
  return {
    Authorization: `Bearer ${idToken}`,
  };
};

export const requestRemovalReport = async (
  patches: Patch[],
  trees: Tree[],
  pastRemovalArea: number | undefined,
  appRequirements: any,
  uploadType: uploadTypesOption,
  shpFileNames: String[],
  adminMode: boolean
) => {
  const { patchesFeatureSet, treesFeatureSet } = createFeatureSets(
    patches,
    trees,
    [],
    nvrTools.removal,
    uploadType
  );
  const authHeaders = await getAuthHeaders();
  const endpoint = adminMode ? adminReportEndpoint : reportEndpoint;
  const result = await fetch(endpoint + "/removal", {
    method: "POST",
    mode: "cors",
    headers: authHeaders,
    body: JSON.stringify({
      patches: patchesFeatureSet.toJSON(),
      trees: treesFeatureSet.toJSON(),
      pastRemovalArea: pastRemovalArea,
      areDetailedFeatures: uploadType === uploadTypes.detailed,
      sourceShapefileNames: shpFileNames,
      ...appRequirements,
    }),
  });
  return await result.json();
};

export const requestOffsetReport = async (
  patches: Patch[],
  trees: Tree[],
  revegetationAreas: RevegetationArea[],
  uploadType: uploadTypesOption,
  shpFileNames: String[],
  adminMode: boolean,
  isOffsetThresholdIgnored: boolean
) => {
  const { patchesFeatureSet, treesFeatureSet, revegAreasFeatureSet } =
    createFeatureSets(
      patches,
      trees,
      revegetationAreas,
      nvrTools.offset,
      uploadType
    );
  const authHeaders = await getAuthHeaders();
  const endpoint = adminMode ? adminReportEndpoint : reportEndpoint;
  const result = await fetch(endpoint + "/offset", {
    method: "POST",
    mode: "cors",
    headers: authHeaders,
    body: JSON.stringify({
      patches: patchesFeatureSet.toJSON(),
      trees: treesFeatureSet.toJSON(),
      revegetationAreas: revegAreasFeatureSet.toJSON(),
      areDetailedFeatures: uploadType === uploadTypes.detailed,
      sourceShapefileNames: shpFileNames,
      isOffsetThresholdIgnored: adminMode
        ? isOffsetThresholdIgnored
        : undefined,
    }),
  });
  return await result.json();
};

export const requestAdminExport = async (
  reportId: string,
  exportType: string
) => {
  const authHeaders = await getAuthHeaders();
  const result = await fetch(adminExportEndpoint, {
    method: "POST",
    mode: "cors",
    headers: authHeaders,
    body: JSON.stringify({
      reportId,
      exportType,
    }),
  });
  return await result.json();
};

export const checkReportStatus = async (
  reportId: string,
  adminMode: boolean
) => {
  const authHeaders = await getAuthHeaders();
  const endpoint = adminMode ? adminReportEndpoint : reportEndpoint;
  const response = await fetch(`${endpoint}/${reportId}`, {
    headers: authHeaders,
  });
  return await response.json();
};

export const checkAdminExportStatus = async (exportId: string) => {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${adminExportEndpoint}/${exportId}`, {
    headers: authHeaders,
  });
  return await response.json();
};

export const downloadReport = async (reportId: string, adminMode: boolean) => {
  const authHeaders = await getAuthHeaders();
  const endpoint = adminMode ? adminReportEndpoint : reportEndpoint;
  const url = `${endpoint}/${reportId}/download`;
  return fetch(url, {
    headers: authHeaders,
  })
    .then((response) => response.json())
    .then((json) => {
      const reportUrl = json.reportUrl;
      const filename = reportUrl.match(/\/([^/?]+)\?/)?.[1] || null;
      return downloadFile(reportUrl, filename);
    });
};

export const downloadAdminExport = async (exportId: string) => {
  const authHeaders = await getAuthHeaders();
  const url = `${adminExportEndpoint}/${exportId}/download`;
  return fetch(url, {
    headers: authHeaders,
  })
    .then((response) => response.json())
    .then((json) => {
      const fileUrl = json.fileUrl;
      const filename = fileUrl.match(/\/([^/?]+\.zip)/)?.[1] || null;
      return downloadFile(fileUrl, filename);
    });
};

export const downloadFile = async (url: string, filename: string) => {
  return fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link: any = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    });
};
